import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import type {QuestionModelV3} from '@albert-io/models';
import Divider from './Divider.react';
import {Item} from './Item.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import classNames from 'classnames';
interface Props {
  question: QuestionModelV3;
}
export const ItemList = ({question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {saveDraftGuess} = useQuestionAndDrafts(question);

  const {
    activeItemId,
    items,
    isDragging,
    hoveredDividerId,
    setActiveItemId,
    moveItemToPosition,
    isSubmitted
  } = useStoreHook(
    useShallow((state) => ({
      items: state.items,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      hoveredDividerId: state.hoveredDividerId,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      isSubmitted: state.isSubmitted
    }))
  );

  const handleItemClick = (itemId: string, _e?: React.MouseEvent) => {
    if (isDragging) return;

    if (activeItemId === itemId) {
      setActiveItemId(null);
    } else {
      setActiveItemId(itemId);
    }
  };

  const handleDividerClick = (dividerIndex: number) => {
    if (isDragging || !activeItemId) return;

    moveItemToPosition(activeItemId, dividerIndex);
    saveDraftGuess();
    setActiveItemId(null);
  };

  return (
    <div
      className={classNames(
        'flex flex-wrap items-center',
        {
          'gap-x3 pl-x3': isSubmitted,
          'gap-y-x2 pl-x0': !isSubmitted
        },
        'rounded-x1 border border-solid border-border-primary background-primary p-x2'
      )}
    >
      {items.map((item, index) => (
        <Item
          key={item.id}
          item={item}
          index={index}
          handleItemClick={handleItemClick}
          handleDividerClick={handleDividerClick}
          question={question}
        />
      ))}
      {/* Trailing divider for inserting after the last item */}
      {!isSubmitted && (
        <Divider
          id={`divider-${items.length}`}
          isVisible={isDragging || activeItemId !== null}
          isHovered={hoveredDividerId === `divider-${items.length}`}
          onClick={() => handleDividerClick(items.length)}
        />
      )}
    </div>
  );
};

export default ItemList;
